import React, { Dispatch, SetStateAction } from 'react';
import {
  View, Text, StyleSheet, FlatList,
} from 'react-native';
import { useSelector } from 'react-redux';
import Colors from '../../../../constants/Colors';
import Style from '../../../../constants/Style';
import BuildingFloorItem from './BuildingFloorItem';
import { Building } from '../AddBuildingModal';
import NoFloorsImage from '../NoFloorsImage';
import { Floor } from '../../../../components/Map/SetFloorsModal/FloorItem';
import ScreenEmptyState from '../../../../components/ScreenEmptyState';
import { UserPermission } from '../../../../constants/Types';

interface BuildingFloorListProps {
  selectedBuilding: Building,
  removeFloor,
  editFloor,
  setSelectedFloorId: Dispatch<SetStateAction<string>>,
  openAddFloorModal: () => void,
}

const BuildingFloorList = ({
  selectedBuilding, removeFloor, setSelectedFloorId, editFloor, openAddFloorModal,
}: BuildingFloorListProps) => {
  const { isAdmin, permissions }: { isAdmin: boolean, permissions: UserPermission[] } = useSelector(
    (state: { user: { isAdmin: boolean, permissions: UserPermission[] } }) => state.user,
  );
  const canAddLocations: boolean = isAdmin || permissions.includes(UserPermission.LOCATIONS_CREATE);
  const happyEnabled: boolean = useSelector((state: { happyEnabled: { enabled: boolean } }) => state
    .happyEnabled.enabled);
  const canUpdateLocations: boolean = isAdmin
    || permissions.includes(UserPermission.LOCATIONS_UPDATE);
  const canDeleteLocations: boolean = isAdmin
    || permissions.includes(UserPermission.LOCATIONS_DELETE);
  const { id }: { id: string, name: string } = useSelector((state:
    { selectedBuilding: { id: string, name: string } }) => state.selectedBuilding);
  const actionsShown: boolean = canUpdateLocations || canDeleteLocations
    || id === selectedBuilding.LocationId;

  return <View
    style={{ flex: 1 }}
    testID='floor-list'
  >
    <View style={[Style.row, styles.header]}>
      <Text style={[styles.parameterTitle, styles.titleSection]}>{'FLOORS'}</Text>
      <Text style={[styles.parameterTitle, styles.prioritySection]}>{'PRIORITY'}</Text>
      <Text style={[styles.parameterTitle, styles.planSection]}>{'PLAN'}</Text>
      <Text style={[styles.parameterTitle, styles.planSection]}>{'AREAS'}</Text>
      <Text style={[styles.parameterTitle, styles.planSection]}>{'ASSETS'}</Text>
      {happyEnabled
        && <Text style={[styles.parameterTitle, styles.planSection]}>{'SENSORS & METERS'}</Text>}
      {actionsShown && <Text style={[styles.parameterTitle, styles.actionSection]}>{'ACTIONS'}</Text>}
    </View>
    <FlatList
      style={styles.list}
      data={Object.values(selectedBuilding?.floors ?? {}).filter((floor: Floor) => !!floor)
        ?.sort((prev: Floor, next: Floor) => prev.Index - next.Index) ?? []}
      renderItem={({ item: floor, index }) => <BuildingFloorItem
        floors={Object.values(selectedBuilding?.floors ?? {})}
        parentName={selectedBuilding.Name}
        {...{
          removeFloor, editFloor, isAdmin, floor, setSelectedFloorId,
        }}
        testID={`floor#${index + 1}`}
      />}
      ListEmptyComponent={<ScreenEmptyState
        image={<NoFloorsImage />}
        title='No floors in this building'
        subtitle='Contact your administrator to add a floor to this building'
        adminSubtitle='Add a floor to this building to have it appear here'
        buttonTitle='Add floor'
        buttonAction={openAddFloorModal}
        actionAvailable={canAddLocations}
      />}
      keyExtractor={(item: Floor) => `floor_list_item_${item.LocationId}`}
    />
  </View>;
};

const styles = StyleSheet.create({
  header: {
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: Colors.light.hereworksWhite500,
    paddingHorizontal: 16,
    marginBottom: 8,
    marginHorizontal: 24,
  },
  parameterTitle: {
    fontSize: 10,
    lineHeight: 15,
    fontFamily: 'Roboto_500Medium',
    color: Colors.light.hereworksBlack400,
    flex: 1,
    alignSelf: 'flex-start',
    textAlign: 'left',
  },
  titleSection: { flex: 0.35 },
  prioritySection: { flex: 0.15 },
  planSection: { flex: 0.08 },
  actionSection: {
    flex: 0.18,
    alignSelf: 'flex-end',
    textAlign: 'right',
  },
  list: {
    paddingHorizontal: 24,
  },
});

export default BuildingFloorList;
